import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import AnimateHeight from "react-animate-height";

import transition from "~/utilities/transition";
import { sanitizeAndParseHTML } from "~/utilities/helpers";
import { mobileMenuFocusManagement } from "~/utilities/accessibilityHelpers";

import BaseLink from "~/components/links/BaseLink";

import IcoIcon from "~/components/IcoIcon";

const Wrapper = styled.div`
    display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
    flex-direction: column;
    margin-bottom: ${({ theme }) => theme.layout.gutter};
`;

const MainNavLink = styled(BaseLink)`
    text-transform: none;
    font-weight: 900;
    font-size: 1.9rem;
    letter-spacing: 0.056em;
    color: ${({ theme }) => theme.palette.darkBlue};
    margin-bottom: 15px;
`;

const SubNavItemsWrapper = styled.div`
    &:not(:last-of-type) {
        margin-bottom: 15px;
    }
`;

const SubLinkWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const SubLink = styled(BaseLink)`
    color: ${({ theme }) => theme.palette.black};
    font-size: 1.9rem;
    font-weigth: 300;
`;

const Trigger = styled.button`
    ${transition({}, "transform")};
    margin-left: 10px;
    transform: ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "none")};
`;

const SubSubWrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin: 15px 0;
`;

const SubSubLink = styled(BaseLink)`
    color: ${({ theme }) => theme.palette.black};
    font-size: 1.5rem;
    font-weigth: 300;

    &:not(:last-of-type) {
        margin-bottom: 10px;
    }
`;

const MainMobileSubNav = ({
    cards = [],
    iconLinks = [],
    isOpen = false,
    linkLabel: topLinkLabel,
    links = [],
    linkUrl,
    tagRef,
    type,
}) => {
    const [openSections, setOpenSections] = useState({});

    const mobileLinks =
        type === "cards"
            ? cards.map(({ buttonLabel, buttonUrl, additionalLinks }) => ({
                  linkLabel: buttonLabel,
                  linkUrl: buttonUrl,
                  subNavigation: additionalLinks,
              }))
            : type === "iconLinks"
              ? iconLinks
              : links;

    const handleClick = (id) => {
        const isSectionOpen = openSections[id];

        setOpenSections({
            [id]: !isSectionOpen,
        });
    };

    useEffect(() => {
        if (isOpen && document.activeElement === document.body) {
            mobileMenuFocusManagement({
                mobileSubNavIsOpen: isOpen,
                mobileNavRef: tagRef,
            });
        }
    }, [isOpen, tagRef]);

    return (
        <Wrapper {...{ isOpen }}>
            {topLinkLabel && linkUrl ? (
                <h2>
                    <MainNavLink
                        tagLabel={`${topLinkLabel}-Mobile-SubTop`}
                        to={linkUrl}
                        tagRef={tagRef.parent}
                    >
                        {topLinkLabel}
                    </MainNavLink>
                </h2>
            ) : null}
            {mobileLinks?.length
                ? mobileLinks.map(
                      (
                          {
                              linkUrl,
                              linkLabel: mobileLinkLabel,
                              subNavigation,
                          },
                          linksIndex,
                      ) => {
                          const triggerProps = {
                              type: "button",
                              "aria-label": `${mobileLinkLabel} submenu`,
                              "aria-expanded":
                                  !!openSections[
                                      `${mobileLinkLabel}-${linksIndex}`
                                  ],
                              onClick: () =>
                                  handleClick(
                                      `${mobileLinkLabel}-${linksIndex}`,
                                  ),
                              isOpen: !!openSections[
                                  `${mobileLinkLabel}-${linksIndex}`
                              ],
                          };

                          const mobileLinksLength = mobileLinks.length - 1;

                          if (
                              linksIndex === mobileLinksLength &&
                              !triggerProps.isOpen
                          ) {
                              triggerProps["data-label"] =
                                  `${topLinkLabel}-Last`;
                          }

                          const subLinkProps = {
                              to: linkUrl,
                          };
                          if (
                              !subNavigation &&
                              linksIndex === mobileLinksLength
                          ) {
                              subLinkProps.tagLabel = `${topLinkLabel}-Last`;
                          }
                          return (
                              <SubNavItemsWrapper
                                  key={`${linksIndex}-${mobileLinkLabel}`}
                              >
                                  <SubLinkWrapper
                                      ref={(el) =>
                                          (tagRef.children.current[linksIndex] =
                                              el)
                                      }
                                  >
                                      <h3>
                                          <SubLink {...subLinkProps}>
                                              {sanitizeAndParseHTML(
                                                  mobileLinkLabel,
                                              )}
                                          </SubLink>

                                          {subNavigation?.length > 0 && (
                                              <Trigger {...triggerProps}>
                                                  <IcoIcon
                                                      icon="caret-down"
                                                      size={14}
                                                  />
                                              </Trigger>
                                          )}
                                      </h3>
                                  </SubLinkWrapper>

                                  {subNavigation?.length ? (
                                      <AnimateHeight
                                          duration={500}
                                          height={
                                              !!openSections[
                                                  `${mobileLinkLabel}-${linksIndex}`
                                              ]
                                                  ? `auto`
                                                  : 0
                                          }
                                      >
                                          <SubSubWrapper>
                                              {subNavigation.map(
                                                  (
                                                      {
                                                          linkLabel:
                                                              subNavLinkLabel,
                                                          linkUrl,
                                                      },
                                                      subNavIndex,
                                                  ) => {
                                                      const subSubLinkProps = {
                                                          key: `${subNavLinkLabel}-${subNavIndex}`,
                                                          to: linkUrl,
                                                      };
                                                      const subNavLength =
                                                          subNavigation.length -
                                                          1;
                                                      const mobileLinksLength =
                                                          mobileLinks.length -
                                                          1;
                                                      if (
                                                          subNavIndex ===
                                                              subNavLength &&
                                                          linksIndex ===
                                                              mobileLinksLength &&
                                                          !!openSections[
                                                              `${mobileLinkLabel}-${linksIndex}`
                                                          ]
                                                      ) {
                                                          subSubLinkProps.tagLabel = `${topLinkLabel}-Last`;
                                                      }
                                                      return (
                                                          <SubSubLink
                                                              {...subSubLinkProps}
                                                          >
                                                              {sanitizeAndParseHTML(
                                                                  subNavLinkLabel,
                                                              )}
                                                          </SubSubLink>
                                                      );
                                                  },
                                              )}
                                          </SubSubWrapper>
                                      </AnimateHeight>
                                  ) : null}
                              </SubNavItemsWrapper>
                          );
                      },
                  )
                : null}
        </Wrapper>
    );
};

MainMobileSubNav.propTypes = {
    linkLabel: PropTypes.string.isRequired,
    linkUrl: PropTypes.string.isRequired,
    type: PropTypes.oneOf(["links", "cards", "iconLinks", "noSubNavigation"])
        .isRequired,
    links: PropTypes.arrayOf(
        PropTypes.shape({
            linkLabel: PropTypes.string.isRequired,
            linkUrl: PropTypes.string.isRequired,
            subNavigation: PropTypes.arrayOf(
                PropTypes.shape({
                    linkUrl: PropTypes.string.isRequired,
                    linkLabel: PropTypes.string.isRequired,
                }),
            ),
        }),
    ),
    cards: PropTypes.arrayOf(
        PropTypes.shape({
            buttonLabel: PropTypes.string,
            buttonUrl: PropTypes.string,
            additionalLinks: PropTypes.arrayOf(
                PropTypes.shape({
                    linkLabel: PropTypes.string.isRequired,
                    linkUrl: PropTypes.string.isRequired,
                }),
            ),
        }),
    ),
    iconLinks: PropTypes.arrayOf(
        PropTypes.shape({
            linkUrl: PropTypes.string.isRequired,
            linkLabel: PropTypes.string.isRequired,
        }),
    ),
    isOpen: PropTypes.bool,
};

export default MainMobileSubNav;
